/**
 * Server-side full Voluum CSV export (Phone tab, limit “All” only).
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback } from 'react';

import { useStatsTrackerParams } from '@/features/trackers/stats-tracker-context';
import { triggerBlobFileDownload } from '@/lib/triggerBlobFileDownload';
import { useToast } from '@/providers/ToastProvider';
import api from '@/services/api';

import type { FiltersState } from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab/shared';
import type { ServerCsvExportOptions } from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab/csv-export/hooks/useCsvExportModalState.types';

import { resolvePhoneStatsCsvDownloadFilename } from './resolvePhoneStatsCsvDownloadFilename';

export interface PhoneStatsServerCsvParams {
  countryKey: string;
  wiserIds: number[];
  from: string;
  to: string;
  currency: string;
  filtersState: FiltersState;
  sort: { field: string; dir: 'asc' | 'desc' } | null;
}

export function usePhoneStatsServerCsvExport(p: PhoneStatsServerCsvParams) {
  const { error: toastError } = useToast();
  const trackerParams = useStatsTrackerParams();

  return useCallback(
    async (
      columnKeys: string[],
      sortOverride?: { field: string; dir: 'asc' | 'desc' } | null,
      options?: ServerCsvExportOptions,
    ) => {
      let sortField: string | undefined;
      let sortDir: 'asc' | 'desc' | undefined;
      if (sortOverride === undefined) {
        if (p.sort?.field && p.sort?.dir) {
          sortField = p.sort.field;
          sortDir = p.sort.dir;
        }
      } else if (sortOverride === null) {
        sortField = undefined;
        sortDir = undefined;
      } else {
        sortField = sortOverride.field;
        sortDir = sortOverride.dir;
      }
      try {
        const res = await api.post<Blob>(
          `smswiser/campaigns/${p.countryKey}/phones-stats-export`,
          {
            wiserIds: p.wiserIds.join(','),
            from: p.from,
            to: p.to,
            tz: 'Europe/Paris',
            currency: p.currency,
            filters: p.filtersState.filters.map((f) => ({
              field: f.field,
              operator: f.operator,
              value: f.value,
            })),
            filtersActive: p.filtersState.active,
            sortField,
            sortDir,
            columns: columnKeys,
            ...(options?.excludeCrossSourceNotedRows === true
              ? { excludeCrossSourceNotedRows: true }
              : {}),
            ...(options?.downloadBaseName
              ? { fileBaseName: options.downloadBaseName }
              : {}),
            ...trackerParams,
          },
          { responseType: 'blob', timeout: 0 },
        );
        const blob = res.data;
        const rawCd = res.headers['content-disposition'];
        const contentDisposition =
          typeof rawCd === 'string' ? rawCd : undefined;
        const fallbackDay = new Date().toISOString().slice(0, 10);
        const filename = resolvePhoneStatsCsvDownloadFilename({
          contentDisposition,
          clientSanitizedBase: options?.downloadBaseName ?? null,
          fallbackIsoDate: fallbackDay,
        });
        triggerBlobFileDownload(blob, filename);
      } catch {
        toastError('CSV export failed. Try a smaller date range or retry.');
        throw new Error('phone-stats-export failed');
      }
    },
    [
      p.countryKey,
      p.wiserIds,
      p.from,
      p.to,
      p.currency,
      p.filtersState,
      p.sort,
      toastError,
      trackerParams,
    ],
  );
}
